import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "../lib/api";
import { Loader2, Check, Copy, Video } from "lucide-react";

const ZOOM_LINKS = {
  orientation: "https://scheduler.zoom.us/casey-muze/braced-call",
  intro: "https://scheduler.zoom.us/casey-muze/coffeewithcasey",
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paystackRef = params.get("reference") || params.get("trxref");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId && !paystackRef) { setState("error"); return; }
    const isPaystack = !sessionId && paystackRef;
    const statusUrl = isPaystack
      ? `${API}/paystack/verify/${encodeURIComponent(paystackRef)}`
      : `${API}/payments/status/${sessionId}`;
    let timer;
    const poll = async () => {
      try {
        const res = await axios.get(statusUrl);
        if (res.data.payment_status === "paid") {
          setData(res.data);
          localStorage.setItem("bnm-entitlement", JSON.stringify({ access_code: res.data.access_code, tiers: res.data.tiers }));
          setState("paid");
          return;
        }
        if (res.data.payment_status === "failed" || res.data.payment_status === "expired") { setState("error"); return; }
      } catch (e) { /* retry */ }
      tries.current += 1;
      if (tries.current > 12) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, paystackRef]);

  const copy = () => {
    if (data?.access_code) { navigator.clipboard.writeText(data.access_code); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center watercolor-wash px-5 safe-top safe-bottom">
      <div className="w-full max-w-md rounded-2xl bg-ivory-50 p-8 text-center shadow-xl ring-1 ring-ivory-400/50" data-testid="payment-success">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-gold-deep" />
            <p className="mt-4 font-display text-lg text-ink">Confirming your purchase…</p>
            <p className="mt-1 text-sm text-ink-muted">One quiet moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gold/15 text-gold-deep"><Check className="h-6 w-6" /></span>
            <h1 className="mt-4 font-display text-2xl text-ink">Thank you — it's yours.</h1>
            <p className="mt-2 text-sm text-ink-soft">Save this access code. It restores your reading on any device.</p>
            <div className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-ivory-200 px-4 py-3">
              <code className="font-display text-xl tracking-wide text-ink" data-testid="access-code">{data?.access_code}</code>
              <button type="button" onClick={copy} aria-label="Copy code" className="rounded-md p-1.5 text-ink-soft hover:text-gold-deep"><Copy className="h-4 w-4" /></button>
            </div>
            {copied && <p className="mt-1 text-xs text-emerald-600">Copied</p>}
            {(data?.tiers || []).some((t) => t === "orientation_client" || t === "immersion_client") && (
              <div className="mt-6 rounded-xl border border-sage/40 bg-sage/10 p-5 text-left" data-testid="orientation-booking">
                <p className="inline-flex items-center gap-2 font-display text-ink"><Video className="h-4 w-4 text-sage-deep" /> Book your 1:1 sessions</p>
                <p className="mt-1 text-sm text-ink-soft">Your journey includes guided sessions with Casey over Zoom. Choose a time that feels gentle.</p>
                <a href={ZOOM_LINKS.orientation} target="_blank" rel="noopener noreferrer" data-testid="zoom-orientation-link"
                  className="debossed-btn mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 font-display text-ink">
                  Schedule your Braced call
                </a>
                <a href={ZOOM_LINKS.intro} target="_blank" rel="noopener noreferrer" data-testid="zoom-intro-link"
                  className="mt-2 block text-center text-sm text-ink-soft underline-offset-2 hover:text-sage-deep hover:underline">
                  Prefer a softer first hello? Book a coffee with Casey
                </a>
              </div>
            )}
            <Link to="/" data-testid="success-open-book" className="debossed-btn mt-6 inline-flex w-full items-center justify-center rounded-full px-6 py-3 font-display text-ink">Open the book</Link>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <h1 className="font-display text-xl text-ink">We're still confirming…</h1>
            <p className="mt-2 text-sm text-ink-soft">If you completed payment, your access code will be ready shortly. You can also restore it from the offers page with the code we create for you.</p>
            <Link to="/get-the-book" className="mt-5 inline-flex items-center justify-center rounded-full border border-ink/20 px-6 py-3 font-display text-ink">Back to offers</Link>
          </>
        )}
      </div>
    </div>
  );
}
